import React, {useEffect, useState} from "react";
import {useSelector} from "../../../../../../../../redux/store";
import {AppStateType} from "../../../../../../../../redux/reducers";
import {
  createButtonSettings,
  fetchButtonSettings,
  updateButtonSettings
} from "../../../../../../../../services/widgetButtonSettingsService";
import Box from "@mui/material/Box";
import {Fade, styled} from "@mui/material";
import {Button, CircularProgress, Grid, MenuItem, TextField} from "@material-ui/core";
import PersonIcon from '../../../../../../../../vendor/person-icon.svg'
import Typography from "@mui/material/Typography";
import ColorPicker from "./components/ColorPicker";
import {GradientButton} from "../../../../../../property-page-components/PropertyForm";
import * as types from "../../../../../../../../constants";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import MarginChanger from "./components/MarginChanger";
import AbsoluteLoading from "../../../../../../../../components/AbsoluteLoading";

const Card = styled(Box)`
  background: rgb(32, 45, 63);
  border: 1px solid rgba(238, 238, 238, 0.04);
  border-radius: 8px;
`

const PreviewText = styled('div')`
  color: rgba(255, 255, 255, 0.67);
  position: absolute;
  top: -30px;
  font-size: 14px !important;
`

const PreviewWidgetButton = styled('button')`
  background-color: rgb(18, 22, 25);
  display: flex;
  -webkit-box-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  align-items: center;
  height: 41px;
  margin: 0 24px 15px 0;
  font-weight: 400;
  padding: 1px 17px;
  border: 1px solid rgb(255, 255, 255);
  border-radius: 40px;
  color: rgb(255, 255, 255);
  font-size: 14px;
  line-height: 16px;
  text-align: center;
  letter-spacing: 0.25px;
  cursor: pointer;
  float: right;
  position: fixed;
  bottom: 0;
  right: 0;
  z-index: 1400000;
  text-transform: unset;
`

const INIT_SETTINGS = {
  background_color: '',
  color: '',
  margin: '0 0 0 0',
  z_index: '0',
  positioning_top: '',
  positioning_left: '',
  positioning_right: '',
  positioning_bottom: '',
  font_family: '',
  font_weight: 400,
  font_size: '',
}

  const POSITIONS: any = {
    top_left: { positioning_top: 0, positioning_left: 0, positioning_right: 'unset', positioning_bottom: 'unset' },
    top_right: { positioning_top: 0, positioning_left: 'unset', positioning_right: 0, positioning_bottom: 'unset' },
    bottom_left: { positioning_top: 'unset', positioning_left: 0, positioning_right: 'unset', positioning_bottom: 0 },
    bottom_right: { positioning_top: 'unset', positioning_left: 'unset', positioning_right: 0, positioning_bottom: 0 },
  }

const ButtonSettingsTab = () => {
  const user = useSelector((state) => state.authReducer.user);
  const property = useSelector((state: AppStateType) => state.propertyReducer.property)!
  const [buttonSettings, setButtonSettings] = useState<any>(INIT_SETTINGS)
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch();
  const { t } = useTranslation()
  const [position, setPosition] = useState('bottom_right')

  const getPosition = (data: any) => {
    let maxMatches = 0;
    let bestPosition = '';

    for (const [key, value] of Object.entries(POSITIONS) as any) {
      let matches = 0;
      for (const prop in value) {
        if (value[prop] === data[prop]) {
          matches++;
        }
      }
      if (matches > maxMatches) {
        maxMatches = matches;
        bestPosition = key;
      }
    }

    return bestPosition;
  }

  useEffect(() => {
    if (property && property.company) {
      setLoading(true)
      fetchButtonSettings(property.company, user!.token).then((data: any) => {
        if (data) {
          setButtonSettings(data)
          const detectPosition = getPosition(data)
          if (detectPosition) {
            setPosition(detectPosition)
          }
        } else {
          setButtonSettings(INIT_SETTINGS)
        }
        setLoading(false)
      }).catch(() => {
        setLoading(false)
      })
    }
  }, []);

  const handleChange = (field: string, value: any) => {
    setButtonSettings({...buttonSettings, [field]: value})
  }

  const getStyle = () => {
    if (buttonSettings) {
      return {
        backgroundColor: buttonSettings.background_color,
        color: buttonSettings.color,
        margin: buttonSettings.margin,
        zIndex: buttonSettings.z_index,
        top: buttonSettings.positioning_top,
        left: buttonSettings.positioning_left,
        right: buttonSettings.positioning_right,
        bottom: buttonSettings.positioning_bottom,
        fontFamily: buttonSettings.font_family,
        fontWeight: buttonSettings.font_weight,
        fontSize: buttonSettings.font_size,
      }
    }
    return {}
  }

  const handleButtonPosition = (position: string) => {
    setPosition(position)
    if (POSITIONS[position]) {
      setButtonSettings({
        ...buttonSettings,
        ...POSITIONS[position]
      })
    }
  }

  const handleSave = () => {
    setLoading(true)
    if (!buttonSettings.company) {
      // create
      createButtonSettings({...buttonSettings, company: property.company}, user!.token).then((data) => {
        if (data) {
          setButtonSettings(data)
          const detectPosition = getPosition(data)
          if (detectPosition) {
            setPosition(detectPosition)
          }
        }
        dispatch({
          type: types.ADD_TOAST,
          payload: {type: 'success', message: t('button_settings_created')}
        })
        setLoading(false)
      }).catch(() => {
        dispatch({
          type: types.ADD_TOAST,
          payload: {}
        })
        setLoading(false)
      })
    } else {
      // update
      updateButtonSettings(property.company as string, buttonSettings, user!.token)
        .then(() => {
          dispatch({
            type: types.ADD_TOAST,
            payload: {type: 'success', message: t('button_settings_updated')}
          })
          setLoading(false)
        }).catch(() => {
          dispatch({
            type: types.ADD_TOAST,
            payload: {}
          })
          setLoading(false)
      })
    }
  }

  return (
    <Box>
      <Fade in={true}>
        <Box mt={4}>
          <Card p={2} position={'relative'}>
            <Grid container spacing={4}>
              <Grid item xs={4}>
                <Box mb={2}>
                  <Typography>Font settings</Typography>
                </Box>
                <Box display={'flex'} flexDirection={'column'}>
                  <Box mb={2.5}>
                    <TextField
                      label={'Font Family'}
                      variant={'outlined'}
                      name={'font_family'}
                      value={buttonSettings.font_family}
                      onChange={(e) => handleChange(e.target.name, e.target.value)}
                      fullWidth
                    />
                  </Box>
                  <Box mb={2.5}>
                    <TextField
                      label={'Font Weight'}
                      select
                      variant={'outlined'}
                      value={buttonSettings.font_weight}
                      onChange={(e) => handleChange('font_weight', e.target.value)}
                      fullWidth
                    >
                      <MenuItem value={300}>300</MenuItem>
                      <MenuItem value={400}>400</MenuItem>
                      <MenuItem value={500}>500</MenuItem>
                      <MenuItem value={600}>600</MenuItem>
                    </TextField>
                  </Box>
                  <Box mb={2.5}>
                    <TextField
                      label={'Font Size'}
                      variant={'outlined'}
                      name={'font_size'}
                      placeholder={'Example: 14px'}
                      value={buttonSettings.font_size}
                      onChange={(e) => handleChange(e.target.name, e.target.value)}
                      fullWidth
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box mb={2}>
                  <Typography>Position settings</Typography>
                </Box>
                <Box display={'flex'} flexDirection={'column'}>
                  <Box mb={2.5}>
                    <TextField
                      select
                      variant={'outlined'}
                      label={'Position'}
                      value={position}
                      onChange={(e) => handleButtonPosition(e.target.value as string)}
                      fullWidth
                    >
                      <MenuItem value={'top_left'}>Top left</MenuItem>
                      <MenuItem value={'top_right'}>Top right</MenuItem>
                      <MenuItem value={'bottom_left'}>Bottom left</MenuItem>
                      <MenuItem value={'bottom_right'}>Bottom right</MenuItem>
                    </TextField>
                  </Box>
                  <Box mb={2.5} display={'flex'}>
                    <TextField
                      label={'Margin'}
                      variant={'outlined'}
                      name={'margin'}
                      placeholder={'Example: 5px 20px 7px 10px (top, right, bottom, left)'}
                      helperText={'5px 5px 5px 5px (top, right, bottom, left)'}
                      value={buttonSettings.margin}
                      onChange={(e) => handleChange(e.target.name, e.target.value)}
                      fullWidth
                    />
                    <MarginChanger
                      value={buttonSettings.margin}
                      onChange={(value) => handleChange('margin', value)}
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box mb={2}>
                  <Typography>Other settings</Typography>
                </Box>
                <Box display={'flex'} flexDirection={'column'}>
                  <Box mb={2.5} display={'flex'}>
                    <TextField
                      label={'Background color'}
                      variant={'outlined'}
                      name={'background_color'}
                      placeholder={'Example: #FFF'}
                      value={buttonSettings.background_color}
                      onChange={(e) => handleChange(e.target.name, e.target.value)}
                      fullWidth
                    />
                    <ColorPicker
                      ml={2}
                      color={buttonSettings.background_color}
                      onChange={(e) => handleChange('background_color', e.hex)}
                    />
                  </Box>
                  <Box mb={2.5} display={'flex'}>
                    <TextField
                      label={'Text color'}
                      variant={'outlined'}
                      name={'color'}
                      placeholder={'Example: #FFF'}
                      value={buttonSettings.color}
                      onChange={(e) => handleChange(e.target.name, e.target.value)}
                      fullWidth
                    />
                    <ColorPicker
                      ml={2}
                      color={buttonSettings.color}
                      defaultColor={'#FFF'}
                      onChange={(e) => handleChange('color', e.hex)}
                    />
                  </Box>
                  <Box mb={2.5}>
                    <TextField
                      label={'Z Index'}
                      variant={'outlined'}
                      name={'z_index'}
                      placeholder={'Example: 5 (Max value: 2147483647)'}
                      helperText={'Use this value if the button is overlapped by an element'}
                      value={buttonSettings.z_index ? buttonSettings.z_index : ''}
                      onChange={(e) => handleChange(e.target.name, e.target.value)}
                      fullWidth
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box display={'flex'} justifyContent={'center'}>
                  <GradientButton onClick={handleSave}>Save</GradientButton>
                </Box>
              </Grid>
            </Grid>
            <AbsoluteLoading visible={loading} />
          </Card>

          <PreviewWidgetButton style={getStyle()}>
            <PreviewText>Preview</PreviewText>
            <img
              src={PersonIcon}
              height={31} width={31}
              style={{
                marginRight: 8,
                color: buttonSettings && buttonSettings.color ? buttonSettings.color : '#FFF',
              }}
            />
            <span>Accessibility</span>
          </PreviewWidgetButton>
        </Box>
      </Fade>
    </Box>
  )
}

export default ButtonSettingsTab